import { useEffect, useMemo, useState } from "react";
import { Dialog } from "@mui/material";
import intl from "react-intl-universal";
import { TbPencil, TbUsersGroup } from "react-icons/tb";
import { ModalCloseButton } from "../../../../../Components/Molecules/ValutDialog";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { useToast } from "../../../../../Hooks/toast";
import {
  BAAS_GROUP_PERMISSIONS,
  BAAS_GROUP_ROLE_PERMISSIONS,
  BAAS_GROUP_ROLES,
  createBaasGroupOnInstances,
  updateBaasGroup,
} from "../../../../../Services/baasUsersApi";

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const disabledInputClass =
  "h-10 w-full cursor-not-allowed rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm text-gray-400 outline-none dark:border-brand-dark-border dark:bg-brand-dark-elevated/50 dark:text-gray-500";

const roleLabelKey = {
  admin: "baas.users.role.admin",
  operator: "baas.users.role.operator",
  viewer: "baas.users.role.viewer",
};

const instancesForMembershipPicker = (instances = []) => {
  const prod = instances.filter(
    (item) => String(item.environment || "").toLowerCase() === "production",
  );
  return prod.length > 0 ? prod : instances;
};

export const BaasGroupCreateModal = ({ open, group = null, onClose, onSaved }) => {
  const { addToast } = useToast();
  const { instances } = useBaasInstance();
  const pickerInstances = useMemo(
    () => instancesForMembershipPicker(instances),
    [instances],
  );
  const isEdit = Boolean(group);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedInstanceIds, setSelectedInstanceIds] = useState([]);
  const [role, setRole] = useState("operator");
  const [permissions, setPermissions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    setFormError(null);

    if (group) {
      setName(group.name ?? "");
      setDescription(group.description ?? "");
      setSelectedInstanceIds(group.instanceId ? [String(group.instanceId)] : []);
      const groupRole = group.role ?? group.roles?.[0] ?? "operator";
      setRole(groupRole);
      setPermissions(group.permissions ?? BAAS_GROUP_ROLE_PERMISSIONS[groupRole] ?? []);
      return;
    }

    setName("");
    setDescription("");
    setSelectedInstanceIds(pickerInstances[0]?.id ? [String(pickerInstances[0].id)] : []);
    setRole("operator");
    setPermissions(BAAS_GROUP_ROLE_PERMISSIONS.operator);
  }, [open, group, pickerInstances]);

  const selectedInstances = useMemo(() => {
    if (isEdit && group) {
      const match =
        instances.find((item) => String(item.id) === String(group.instanceId)) ??
        instances.find(
          (item) => Number(item.baasInstanceId) === Number(group.baasInstanceId),
        );
      return match ? [match] : [];
    }

    return pickerInstances.filter((instance) =>
      selectedInstanceIds.includes(String(instance.id)),
    );
  }, [isEdit, group, instances, pickerInstances, selectedInstanceIds]);

  const resetForm = () => {
    setName("");
    setDescription("");
    setSelectedInstanceIds([]);
    setRole("operator");
    setPermissions(BAAS_GROUP_ROLE_PERMISSIONS.operator);
    setSubmitting(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const toggleInstance = (instanceId) => {
    if (isEdit) return;
    const id = String(instanceId);
    setSelectedInstanceIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  };

  const handleRoleChange = (nextRole) => {
    setRole(nextRole);
    setPermissions(BAAS_GROUP_ROLE_PERMISSIONS[nextRole] ?? []);
  };

  const togglePermission = (permission) => {
    setPermissions((current) =>
      current.includes(permission)
        ? current.filter((item) => item !== permission)
        : [...current, permission],
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!name.trim() || selectedInstances.length === 0) {
      return;
    }

    setSubmitting(true);
    setFormError(null);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        role,
        permissions,
      };

      if (isEdit) {
        const targetInstance = selectedInstances[0];
        if (!targetInstance) return;
        await updateBaasGroup(targetInstance, group.id, payload);
        addToast({
          type: "success",
          title: intl.get("baas.groups.editSuccess"),
        });
      } else {
        await createBaasGroupOnInstances(selectedInstances, payload);
        addToast({
          type: "success",
          title: intl.get("baas.groups.createSuccess"),
        });
      }

      onSaved?.();
      handleClose();
    } catch (error) {
      setFormError(error?.detail || error?.message || intl.get("baas.groups.saveError"));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit} className="relative p-6">
        <ModalCloseButton
          onClick={handleClose}
          className="absolute right-3 top-3"
          disabled={submitting}
        />
        <div className="mb-5 flex items-center gap-3 pr-8">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-gold/10 text-brand-gold">
            {isEdit ? <TbPencil size={20} /> : <TbUsersGroup size={20} />}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-brand-dark dark:text-gray-100">
              {intl.get(isEdit ? "baas.groups.editTitle" : "baas.groups.createTitle")}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {intl.get(isEdit ? "baas.groups.editSubtitleOrg" : "baas.groups.createSubtitleOrg")}
            </p>
          </div>
        </div>

        {formError ? (
          <p
            role="alert"
            className="mb-4 rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
          >
            {formError}
          </p>
        ) : null}

        <div className="space-y-4">
          <div>
            <p className="mb-2 text-sm font-medium text-brand-dark dark:text-gray-200">
              {intl.get("baas.groups.instancesLabel")}
            </p>
            {isEdit ? (
              <input
                type="text"
                value={
                  selectedInstances[0]?.name ??
                  group?.instanceId ??
                  ""
                }
                className={disabledInputClass}
                disabled
                readOnly
              />
            ) : (
              <div className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-gray-200 p-3 dark:border-brand-dark-border">
                {pickerInstances.map((instance) => {
                  const checked = selectedInstanceIds.includes(String(instance.id));
                  return (
                    <label
                      key={instance.id}
                      className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-gray-700 transition hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-brand-dark-elevated/40"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleInstance(instance.id)}
                        className="h-4 w-4 rounded border-gray-300 text-brand-gold focus:ring-brand-gold"
                      />
                      <span className="min-w-0 flex-1 truncate font-medium">{instance.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
            {!isEdit ? (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {intl.get("baas.groups.instancesHint")}
              </p>
            ) : null}
          </div>

          <div>
            <label
              htmlFor="group-name"
              className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-200"
            >
              {intl.get("baas.groups.nameLabel")}
            </label>
            <input
              id="group-name"
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={intl.get("baas.groups.namePlaceholder")}
              className={inputClass}
              required
            />
          </div>

          <div>
            <label
              htmlFor="group-role"
              className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-200"
            >
              {intl.get("baas.groups.roleLabel")}
            </label>
            <select
              id="group-role"
              value={role}
              onChange={(event) => handleRoleChange(event.target.value)}
              className={inputClass}
            >
              {BAAS_GROUP_ROLES.map((roleOption) => (
                <option key={roleOption} value={roleOption}>
                  {intl.get(roleLabelKey[roleOption])}
                </option>
              ))}
            </select>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-brand-dark dark:text-gray-200">
              {intl.get("baas.groups.permissionsLabel")}
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {BAAS_GROUP_PERMISSIONS.map((permission) => (
                <label
                  key={permission}
                  className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 transition hover:border-brand-gold/40 dark:border-brand-dark-border dark:text-gray-300"
                >
                  <input
                    type="checkbox"
                    checked={permissions.includes(permission)}
                    onChange={() => togglePermission(permission)}
                    className="h-4 w-4 rounded border-gray-300 text-brand-gold focus:ring-brand-gold"
                  />
                  <span className="font-mono text-xs">{permission}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label
              htmlFor="group-description"
              className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-200"
            >
              {intl.get("baas.groups.descriptionLabel")}
              <span className="ml-1 font-normal text-gray-400">
                ({intl.get("baas.groups.descriptionOptional")})
              </span>
            </label>
            <textarea
              id="group-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={intl.get("baas.groups.descriptionPlaceholder")}
              rows={3}
              className={`${inputClass} h-auto py-2`}
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="submit"
            className="btn-primary"
            disabled={submitting || selectedInstances.length === 0}
          >
            {intl.get(isEdit ? "baas.groups.editGroup" : "baas.groups.createGroup")}
          </button>
        </div>
      </form>
    </Dialog>
  );
};
